import { useEffect, useState } from "react";
import { Button, Empty, EnvBadge, ErrorNotice, Field, Input, Loading, Panel, Select, StatusBadge, useToast } from "../../components/ui";
import { gameApi } from "../../lib/api";
import { formatMoney, parseMoney } from "../../lib/money";
import { invalidate, useAction, useQuery } from "../../lib/query";
import { useClient } from "../../lib/session";
import type { Bank, LimitPolicy } from "../../lib/types";
import type { GameTabProps } from "./Game";

const SCOPES: { id: "player_funding" | "game_funding" | "account_transfer"; label: string; help: string }[] = [
  { id: "player_funding", label: "Funding per player", help: "How much one player can be paid" },
  { id: "game_funding", label: "Funding for the whole game", help: "Total the game can issue" },
  { id: "account_transfer", label: "Transfers per account", help: "How much one account can send" },
];

export function GameBanks({ game, env }: GameTabProps) {
  const api = gameApi(useClient(), { gameId: game.id, env });
  const toast = useToast();
  const banks = useQuery(`dev:banks:${game.id}:${env}`, () => api.banks());
  const currencies = useQuery(`dev:currencies:${game.id}:${env}`, () => api.currencies());
  const [name, setName] = useState("");
  const [cur, setCur] = useState("NGN");
  const [code, setCode] = useState("");
  const [color, setColor] = useState("#0F6E5F");
  const [selected, setSelected] = useState<string | null>(null);
  const create = useAction(() => api.createBank({ name: name.trim(), base_currency: cur, bank_code: code.trim() || undefined, branding: { primary_color: color } }));
  const list = banks.data?.data ?? [];
  const bank = list.find((b) => b.id === selected) ?? list[0];

  return (
    <div className="stack">
      <Panel title={<>Create a <EnvBadge env={env} /> bank</>} sub="Each bank gets a 3-digit code, its own account numbers and eight ledger accounts for its books (including fee income and levies payable).">
        <form
          className="form"
          onSubmit={async (e) => {
            e.preventDefault();
            const b = await create.run();
            if (b) {
              setName("");
              setCode("");
              setSelected(b.id);
              invalidate(`dev:banks:${game.id}`);
              toast(`Created ${b.name} (code ${b.bank_code})`);
            }
          }}
        >
          <div className="form-row">
            <Field label="Bank name"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Eko Trust Bank" required maxLength={80} /></Field>
            <Field label="Currency">
              <Select value={cur} onChange={(e) => setCur(e.target.value)}>
                {(currencies.data?.data ?? []).map((c) => (
                  <option key={c.code} value={c.code}>{c.code} ({c.name}{c.standard ? "" : ", custom"})</option>
                ))}
              </Select>
            </Field>
            <Field label="Bank code" hint="3 digits; leave empty to assign one"><Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 3))} inputMode="numeric" placeholder="058" /></Field>
            <Field label="Brand colour"><Input type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ padding: 4 }} /></Field>
          </div>
          <ErrorNotice error={create.error} />
          <div><Button type="submit" variant="primary" busy={create.pending} disabled={!name.trim()}>Create bank</Button></div>
        </form>
      </Panel>

      <Panel title="Banks" flush>
        <ErrorNotice error={banks.error} onRetry={banks.reload} />
        {banks.loading && !banks.data && <Loading />}
        {banks.data && list.length === 0 && <Empty title={`No ${env} banks yet`}>Create one above.</Empty>}
        {list.length > 0 && (
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Bank</th><th>Code</th><th>Currency</th><th>Features</th><th>Status</th></tr></thead>
              <tbody>
                {list.map((b) => (
                  <tr key={b.id} className="clickable" onClick={() => setSelected(b.id)} aria-selected={bank?.id === b.id} style={bank?.id === b.id ? { boxShadow: "inset 3px 0 0 var(--role)" } : undefined}>
                    <td>
                      <div className="row" style={{ gap: 8 }}>
                        <span aria-hidden="true" style={{ width: 12, height: 12, borderRadius: 3, background: String(b.branding.primary_color ?? "var(--line-strong)") }} />
                        <span className="cell-title">{b.name}</span>
                      </div>
                      <div className="cell-sub">Config version {b.config_version}</div>
                    </td>
                    <td className="mono">{b.bank_code}</td>
                    <td>{b.base_currency.code}</td>
                    <td className="small">{Object.entries(b.feature_flags).filter(([, v]) => v).map(([k]) => k).join(", ")}</td>
                    <td><StatusBadge status={b.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {bank && <BankSettings key={bank.id} gameId={game.id} env={env} bank={bank} />}
    </div>
  );
}

function BankSettings({ gameId, env, bank }: { gameId: string; env: GameTabProps["env"]; bank: Bank }) {
  const api = gameApi(useClient(), { gameId, env });
  const toast = useToast();
  const limits = useQuery(`dev:limits:${bank.id}`, () => api.limits(bank.id));
  const [draft, setDraft] = useState<Record<string, { per: string; daily: string }>>({});
  const c = bank.base_currency;
  const fmt = (v: number | null | undefined) => (v == null ? "" : formatMoney(v, { code: c.code, exponent: c.exponent, symbol: null }).replace(` ${c.code}`, ""));

  useEffect(() => {
    if (!limits.data) return;
    const d: Record<string, { per: string; daily: string }> = {};
    for (const s of SCOPES) {
      const p = limits.data.limits[s.id];
      d[s.id] = { per: fmt(p?.per_transaction_max), daily: fmt(p?.daily_max) };
    }
    setDraft(d);
  }, [limits.data]); // fmt is stable for this bank

  const save = useAction(async () => {
    const body: Record<string, LimitPolicy | null> = {};
    for (const s of SCOPES) {
      const d = draft[s.id] ?? { per: "", daily: "" };
      const per = d.per.trim() ? parseMoney(d.per, c.exponent) : null;
      const daily = d.daily.trim() ? parseMoney(d.daily, c.exponent) : null;
      if ((d.per.trim() && !per) || (d.daily.trim() && !daily)) throw new Error(`Check the amounts for "${s.label}": use numbers with at most ${c.exponent} decimal places.`);
      body[s.id] = per || daily ? { per_transaction_max: per, daily_max: daily } : null;
    }
    return api.setLimits(bank.id, body);
  });
  const flags = useAction((f: Record<string, boolean>) => api.updateBank(bank.id, { feature_flags: f }));

  return (
    <div className="grid-2">
      <Panel title={`Limits for ${bank.name}`} sub={`In ${c.code}. Leave a box empty for no limit. Daily caps reset at the game-day boundary.`}>
        <ErrorNotice error={limits.error} onRetry={limits.reload} />
        {limits.data && (
          <form
            className="form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (await save.run()) {
                invalidate(`dev:limits:${bank.id}`);
                toast("Limits saved");
              }
            }}
          >
            {SCOPES.map((s) => (
              <div key={s.id}>
                <div className="small" style={{ fontWeight: 600 }}>{s.label}</div>
                <div className="cell-sub" style={{ marginBottom: 6 }}>{s.help}</div>
                <div className="form-row">
                  <Field label="Per transaction">
                    <Input inputMode="decimal" value={draft[s.id]?.per ?? ""} onChange={(e) => setDraft({ ...draft, [s.id]: { ...draft[s.id], per: e.target.value } })} placeholder="No limit" />
                  </Field>
                  <Field label="Per game day">
                    <Input inputMode="decimal" value={draft[s.id]?.daily ?? ""} onChange={(e) => setDraft({ ...draft, [s.id]: { ...draft[s.id], daily: e.target.value } })} placeholder="No limit" />
                  </Field>
                </div>
              </div>
            ))}
            <p className="small muted">
              Game day {limits.data.game_day}: the game has issued {formatMoney(limits.data.game_funding_used_today, { code: c.code, exponent: c.exponent, symbol: c.symbol })} so far.
            </p>
            <ErrorNotice error={save.error} />
            <div><Button type="submit" variant="primary" busy={save.pending}>Save limits</Button></div>
          </form>
        )}
      </Panel>
      <Panel title="Features and books">
        <div className="stack" style={{ gap: 16 }}>
          <div className="checks">
            {(["transfers", "holds"] as const).map((f) => (
              <label key={f}>
                <input
                  type="checkbox"
                  checked={bank.feature_flags[f] !== false}
                  disabled={flags.pending}
                  onChange={async (e) => {
                    if (await flags.run({ [f]: e.target.checked })) {
                      invalidate(`dev:banks:${gameId}`);
                      toast(`${f === "transfers" ? "Transfers" : "Holds"} ${e.target.checked ? "enabled" : "disabled"}`);
                    }
                  }}
                />
                {f === "transfers" ? "Allow transfers" : "Allow holds"}
              </label>
            ))}
          </div>
          <ErrorNotice error={flags.error} />
          <SystemAccounts gameId={gameId} env={env} bankId={bank.id} />
        </div>
      </Panel>
    </div>
  );
}

function SystemAccounts({ gameId, env, bankId }: { gameId: string; env: GameTabProps["env"]; bankId: string }) {
  const api = gameApi(useClient(), { gameId, env });
  const q = useQuery(`dev:bank:${bankId}`, () => api.bank(bankId));
  const accts = q.data?.system_accounts ?? [];
  return (
    <div>
      <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>The bank's own ledger accounts</div>
      <ErrorNotice error={q.error} />
      <table className="data">
        <tbody>
          {accts.map((a) => (
            <tr key={a.id}>
              <td>{(a.system_role ?? "").replace(/_/g, " ")}</td>
              <td className="acct-no">{a.account_number}</td>
              <td className="num"><span className={`amt ${a.posted_balance < 0 ? "debit" : ""}`}>{formatMoney(a.posted_balance, a.currency)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="cell-sub" style={{ marginTop: 8 }}>Issuance runs negative by exactly the money in circulation, so the bank always nets to zero.</p>
    </div>
  );
}
